import { useEffect, useMemo, useState } from 'react';
import { skillsApi } from '../../../api/talent.js';
import { hrApi } from '../../../api/hr.js';

/**
 * Skills Cloud — taxonomy editor + employee skill profile with endorsements.
 */
export default function Skills() {
  const [skills, setSkills] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [profileEmp, setProfileEmp] = useState('');
  const [profile, setProfile] = useState(null);

  function refresh() {
    setLoading(true);
    Promise.all([skillsApi.listSkills(), hrApi.listEmployees().catch(() => ({ items: [] }))])
      .then(([s, e]) => {
        setSkills(s?.items || []);
        setEmployees(e?.items || e || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  const roots = useMemo(() => skills.filter((s) => !s.parent_skill_id), [skills]);
  const childrenOf = (id) => skills.filter((s) => s.parent_skill_id === id);

  async function createSkill(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await skillsApi.createSkill({
        name: fd.get('name'),
        category: fd.get('category') || null,
        description: fd.get('description') || null,
        parentSkillId: fd.get('parentSkillId') || null,
      });
      e.target.reset();
      setShowCreate(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create skill');
    }
  }

  async function loadProfile(employeeId) {
    setProfileEmp(employeeId);
    setProfile(null);
    if (!employeeId) return;
    try {
      setProfile(await skillsApi.getProfile(employeeId));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load profile');
    }
  }

  async function endorse(esId) {
    try {
      await skillsApi.endorse(esId, { comment: 'Endorsed' });
      // Refetch the profile IN PLACE (no null-blank) so the endorsement count
      // updates without unmounting the table.
      if (profileEmp) setProfile(await skillsApi.getProfile(profileEmp));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to endorse');
    }
  }

  const SkillRow = ({ s, depth }) => (
    <>
      <tr data-testid={`talent-skill-row-${s.id}`}>
        <td style={{ paddingLeft: 12 + depth * 24 }}>{depth > 0 ? '↳ ' : ''}{s.name}</td>
        <td>{s.category || '—'}</td>
        <td>{s.description || '—'}</td>
      </tr>
      {childrenOf(s.id).map((c) => <SkillRow key={c.id} s={c} depth={depth + 1} />)}
    </>
  );

  return (
    <section data-testid="talent-skills-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Skills Cloud</h1>
          <p className="muted">{skills.length} skills in the taxonomy</p>
        </div>
        <button className="btn primary" data-testid="talent-skill-create" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Close' : '+ Skill'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="talent-skills-error">{error}</div>}

      {showCreate && (
        <form onSubmit={createSkill} className="card form-card grid-form">
          <label>Name<input name="name" data-testid="talent-skill-name-input" required /></label>
          <label>Category / Role<input name="category" data-testid="talent-skill-category-input" /></label>
          <label>Parent skill
            <select name="parentSkillId" defaultValue="">
              <option value="">— none (root) —</option>
              {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="full">Description<input name="description" /></label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="skills-add">Add skill</button></div>
        </form>
      )}

      <div className="card">
        <h3>Taxonomy</h3>
        <table className="data-table" data-testid="talent-skill-list">
          <thead><tr><th>Skill</th><th>Category</th><th>Description</th></tr></thead>
          <tbody>
            {roots.map((s) => <SkillRow key={s.id} s={s} depth={0} />)}
            {!loading && skills.length === 0 && <tr><td colSpan={3} className="muted">No skills yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Employee skill profile</h3>
        <label>Employee
          <select data-testid="talent-profile-employee-input" value={profileEmp} onChange={(e) => loadProfile(e.target.value)}>
            <option value="">— select —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
        </label>
        {profile && (
          <table className="data-table" data-testid="talent-profile-list">
            <thead><tr><th>Skill</th><th>Proficiency</th><th>Endorsements</th><th></th></tr></thead>
            <tbody>
              {profile.skills.map((s) => (
                <tr key={s.id} data-testid={`talent-profile-row-${s.id}`}>
                  <td>{s.skill_name}</td>
                  <td>{s.proficiency}/5 {s.self_rated ? '(self)' : '(validated)'}</td>
                  <td data-testid={`talent-endorsement-count-${s.id}`}>{s.endorsement_count}</td>
                  <td><button className="btn ghost" data-testid={`talent-endorse-${s.id}`} onClick={() => endorse(s.id)}>Endorse</button></td>
                </tr>
              ))}
              {profile.skills.length === 0 && <tr><td colSpan={4} className="muted">No skills recorded.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
